/**
 * Atlas Iframe Service
 *
 * Handles communication between the Vue.js parent application and the
 * React/Next.js Atlas frontend embedded via iframe.
 */

import { getIdentityToken } from '@/services/http';

// Message types sent from parent (Vue) to child (React)
export type ParentMessageType = 'AUTH_TOKEN' | 'NAVIGATE' | 'THEME_SYNC' | 'CONTEXT_UPDATE';

// Message types received from child (React) to parent (Vue)
export type ChildMessageType = 'READY' | 'REQUEST_AUTH' | 'NAVIGATION' | 'ACTION_COMPLETE' | 'RESIZE' | 'PARENT_NAVIGATE' | 'PRINT' | 'QUERY_PARAMS';

export interface ParentMessage {
  type: ParentMessageType;
  payload: unknown;
  source: 'warbler-parent';
}

export interface ChildMessage {
  type: ChildMessageType;
  payload: unknown;
  source: 'atlas-frontend';
}

export interface AtlasIframeConfig {
  /** The base URL of the Atlas frontend */
  baseUrl: string;
  /** Callback when iframe is ready */
  onReady?: () => void;
  /** Callback when auth is requested */
  onAuthRequest?: () => void;
  /** Callback when navigation changes in iframe */
  onNavigation?: (route: string) => void;
  /** Callback when an action completes */
  onActionComplete?: (action: string, result: unknown) => void;
  /** Callback when resize is requested */
  onResize?: (height: number) => void;
  /** Callback when Atlas requests navigation in the parent app */
  onParentNavigate?: (path: string) => void;
  /** Callback when Atlas requests printing */
  onPrint?: (html: string, title: string) => void;
  /** Callback when Atlas updates its URL query parameters */
  onQueryParams?: (queryString: string) => void;
}

type MessageHandler<T = unknown> = (payload: T) => void;

class AtlasIframeService {
  private iframe: HTMLIFrameElement | null = null;
  private config: AtlasIframeConfig;
  private messageHandlers: Map<ChildMessageType, MessageHandler[]> = new Map();
  private boundMessageHandler: (event: MessageEvent) => void;

  constructor(config?: Partial<AtlasIframeConfig>) {
    const defaultBaseUrl = process.env.VUE_APP_ATLAS_FRONTEND_URL || 'http://localhost:3000';

    this.config = {
      baseUrl: defaultBaseUrl,
      ...config,
    };

    this.boundMessageHandler = this.handleMessage.bind(this);
  }

  /**
   * Get the allowed origins for message validation
   */
  private get allowedOrigins(): string[] {
    return [
      this.config.baseUrl,
      'http://localhost:3000', // Next.js dev server
      'https://atlas-ui.assisty.ai',
      'https://staging-atlas-ui.assisty.ai',
    ];
  }

  /**
   * Initialize the service with an iframe element
   */
  initialize(iframe: HTMLIFrameElement): void {
    this.iframe = iframe;
    window.addEventListener('message', this.boundMessageHandler);

    // Set up default handlers from config
    if (this.config.onReady) {
      this.on('READY', this.config.onReady);
    }
    if (this.config.onAuthRequest) {
      this.on('REQUEST_AUTH', () => {
        this.sendAuthToken();
        this.config.onAuthRequest?.();
      });
    }
    if (this.config.onNavigation) {
      this.on('NAVIGATION', (payload: { route: string }) => {
        this.config.onNavigation?.(payload.route);
      });
    }
    if (this.config.onActionComplete) {
      this.on('ACTION_COMPLETE', (payload: { action: string; result: unknown }) => {
        this.config.onActionComplete?.(payload.action, payload.result);
      });
    }
    if (this.config.onResize) {
      this.on('RESIZE', (payload: { height: number }) => {
        this.config.onResize?.(payload.height);
      });
    }
    if (this.config.onParentNavigate) {
      this.on('PARENT_NAVIGATE', (payload: { path: string }) => {
        this.config.onParentNavigate?.(payload.path);
      });
    }
    if (this.config.onQueryParams) {
      this.on('QUERY_PARAMS', (payload: { queryString: string }) => {
        this.config.onQueryParams?.(payload.queryString);
      });
    }

    // Always handle PRINT — open print window from parent context (outside iframe sandbox)
    // Clear any existing PRINT handlers to avoid duplicates if initialize() is called again
    this.messageHandlers.delete('PRINT');
    this.on('PRINT', (payload: unknown) => {
      // Validate payload before using
      const p = payload as Record<string, unknown> | null;
      if (!p || typeof p.html !== 'string' || !p.html) {
        console.warn('[AtlasIframeService] PRINT message received with invalid payload', payload);
        return;
      }
      const html = p.html;
      const title = typeof p.title === 'string' ? p.title : 'Purchase Order';

      if (this.config.onPrint) {
        this.config.onPrint(html, title);
      } else {
        // Default print handler — use Blob URL so the content runs in an opaque
        // origin with no access to the parent window (avoids XSS from postMessage)
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
        if (printWindow) {
          printWindow.addEventListener('afterprint', () => printWindow.close());
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
          }, 400);
        }
        // Revoke after a delay to allow the window to load
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
    });
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    window.removeEventListener('message', this.boundMessageHandler);
    this.messageHandlers.clear();
    this.iframe = null;
  }

  /**
   * Handle incoming messages from iframe
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin
    if (!this.isValidOrigin(event.origin)) {
      return;
    }

    const message = event.data as ChildMessage;

    // Validate message structure
    if (!message || message.source !== 'atlas-frontend' || !message.type) {
      return;
    }

    // Handle REQUEST_AUTH automatically if no custom handler
    if (message.type === 'REQUEST_AUTH' && !this.messageHandlers.has('REQUEST_AUTH')) {
      this.sendAuthToken();
      return;
    }

    // Dispatch to registered handlers
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => handler(message.payload));
    }
  }

  /**
   * Validate message origin
   */
  private isValidOrigin(origin: string): boolean {
    return this.allowedOrigins.some(
      (allowed) => origin === allowed || origin.endsWith('.assisty.ai')
    );
  }

  /**
   * Register a handler for a message type
   */
  on<T = unknown>(type: ChildMessageType, handler: MessageHandler<T>): () => void {
    const handlers = this.messageHandlers.get(type) || [];
    handlers.push(handler as MessageHandler);
    this.messageHandlers.set(type, handlers);

    // Return unsubscribe function
    return () => {
      const currentHandlers = this.messageHandlers.get(type) || [];
      const index = currentHandlers.indexOf(handler as MessageHandler);
      if (index > -1) {
        currentHandlers.splice(index, 1);
        this.messageHandlers.set(type, currentHandlers);
      }
    };
  }

  /**
   * Send a message to the iframe
   */
  private sendToIframe(type: ParentMessageType, payload: unknown = {}): void {
    if (!this.iframe?.contentWindow) {
      console.warn('[AtlasIframeService] No iframe available');
      return;
    }

    const message: ParentMessage = {
      type,
      payload,
      source: 'warbler-parent',
    };

    this.iframe.contentWindow.postMessage(message, this.config.baseUrl);
  }

  /**
   * Send authentication token to iframe
   */
  sendAuthToken(): void {
    const token = getIdentityToken();
    if (token) {
      this.sendToIframe('AUTH_TOKEN', { token });
    } else {
      console.warn('[AtlasIframeService] No token available to send');
    }
  }

  /**
   * Navigate to a specific agent in the iframe
   */
  navigateToAgent(agent: string): void {
    this.sendToIframe('NAVIGATE', { agent });
  }

  /**
   * Sync theme with iframe
   */
  syncTheme(theme: 'light' | 'dark'): void {
    this.sendToIframe('THEME_SYNC', { theme });
  }

  /**
   * Send context data to iframe
   */
  sendContext(data: unknown): void {
    this.sendToIframe('CONTEXT_UPDATE', { data });
  }

  /**
   * Get the iframe source URL for a specific agent
   */
  getIframeSrc(agent?: string): string {
    if (agent) {
      return `${this.config.baseUrl}/${agent}`;
    }
    return this.config.baseUrl;
  }

  /**
   * Update the configuration
   */
  updateConfig(config: Partial<AtlasIframeConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Export a factory function for creating service instances
export function createAtlasIframeService(config?: Partial<AtlasIframeConfig>): AtlasIframeService {
  return new AtlasIframeService(config);
}

// Export a singleton for simple use cases
export const atlasIframeService = new AtlasIframeService();

export default atlasIframeService;
